import React, { useState, useContext, useRef } from "react";
import axios from "axios";
import * as braze from "@braze/web-sdk";
import { BreakpointContext } from "components/common";
import AutoplayCarousel from "common/AutoplayCarousel";
import Email from "common/EmailV2";
import { Box } from "@mui/material";
import { isValidEmail, useQueryParamsAsJson } from "@tomocredit/common-fe/utils";
import { LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import BoostHeader from "components/BoostHeader";
import TestimonialCard1 from "images/iq/testimonial-1.webp";
import TestimonialCard2 from "images/iq/testimonial-2.webp";
import TestimonialCard3 from "images/iq/testimonial-3.webp";
import TestimonialCard4 from "images/iq/testimonial-4.webp";
import EmailBot1 from "images/iq/email-bot-1.webp";
import EmailBot2 from "images/iq/email-bot-2.webp";
import EmailBot3 from "images/iq/email-bot-3.webp";
import ActionCard1 from "images/iq/action-card-1.webp";
import ActionCard2 from "images/iq/action-card-2.webp";
import ActionCard3 from "images/iq/action-card-3.webp";
import ActionCard4 from "images/iq/action-card-4.webp";
import IQHero from "images/iq/iq-hero.webp";
import TomoIQVideo from "images/iq/TOMOIQ_VIDEO.mp4";
import VideoStartImage from "images/iq/start-playing-image.gif";
import { LandingDefault } from "./LandingHelper";
import { socialProofImages } from "../Boost/utils";
import { ActionCard } from "./ActionCard";
import { TomoIQWaitlistModal } from "./TomoIQWaitlistModal";

const baseDomain = "https://www.freeform.chat";

const ROUTES_WEBSITE = "tomo-lead-gen";
const ROUTES_APP = "tomo-app";

const routes = [ROUTES_WEBSITE, ROUTES_APP];

const IQLanding = ({
  match: {
    params: { platform },
  },
}) => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");
  const belowLg = isBelow("lg");
  const videoRef = useRef(null);
  const [renderChat, setRenderChat] = useState(false);
  const { capturedUrlParams } = useQueryParamsAsJson(['email']);
  const [renderWaitlistModal, setRenderWaitlistModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);
  const currentRoute = routes.find((r) => r === platform) || ROUTES_WEBSITE;
  const link = `${baseDomain}/${currentRoute}`;

  const handlePlay = () => {
    setVideoPlaying(true);
    videoRef.current.play();
  };

  const handleActionCardButtonClick = (cardNum) => {
    window.gtag?.("event", "IQ_CARD_CTA_CLICKED", { cardNum });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEmailSubmit = async (email) => {
    setSubmitError("");
    if (!email) {
      return;
    }
    if (!isValidEmail(email)) {
      setSubmitError("Please enter a valid email");
      return;
    }
    try {
      window.gtag?.("event", "IQ_EMAIL_SUBMITTED", { email });
      const submitRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/users/newUserFromLanding`, {
        userId: email,
        source: LEAD_SOURCES.IQ_LANDING,
        capturedUrlParams,
      });
      if (submitRes.status === 200) {
        const { guid } = submitRes.data || {};
        if (guid) {
          // Braze identify user
          braze.changeUser(guid);
          // Braze set user email attr
          braze.getUser()?.setEmail(email);
        }
        setRenderWaitlistModal(true);
      }
    } catch (error) {
      let errorMessage = "Could not communicate with server. Please try again later";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setSubmitError(errorMessage);
    }
  };

  return (
    <div className="flex flex-col items-center w-full" style={{ backgroundColor: "#1F1D24", overflowX: "hidden", letterSpacing: "-0.25px" }}>
      {/* HEADER SECTION */}
      <div className="headerBar fixed"><BoostHeader /></div>
      <div className="my-10 flex flex-col items-center" style={{ paddingTop: belowMd ? 60 : 84 }}>
        {/* IQ LANDING SECTION */}
        <div
          className="flex flex-col-reverse md:flex-row justify-between md:justify-normal items-center px-6 md:px-64 md:mx-auto"
        >
          <div className="pt-10 flex flex-col" style={{ maxWidth: belowMd ? undefined : 740 }}>
            <div
              className="text-white hidden md:block whitespace-nowrap"
              style={{ fontSize: belowLg ? 72 : 92, fontWeight: 900 }}>
              tomo
              <span className="text-boost-primary-500">IQ</span>
              .
            </div>
            {/* for some reason textclip does not work properly if implemented as belowMd ? style1 : style 2 */}
            {belowMd && (
              <div
                className="font-bold"
                style={{
                  fontSize: 20,
                  background: "linear-gradient(118.11deg, #E3DBFD 65.74%, #BDA8FF 90.46%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  webkitTextFillColor: "transparent",
                  textAlign: "center",
                }}
              >
                Let AI Take Over Your Finance
              </div>
            )}
            {!belowMd && (
              <div
                className="font-bold"
                style={{
                  fontSize: belowLg ? 30 : 40,
                  background: "linear-gradient(90deg, #FFFFFF 20%, #BBA7F0 70%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  webkitTextFillColor: "transparent",
                  lineHeight: "normal",
                  width: "100%",
                }}
              >
                <p className="whitespace-nowrap">Let AI Take Over Your Finance</p>
              </div>
            )}
            <div className="w-full mt-4 self-center md:self-start" style={{ maxWidth: 433 }}>
              <div className="flex w-full relative" style={{ height: belowMd ? 62 : 95 }}>
                <img alt="" src={EmailBot1} className="" />
                <span className="flex space-x-1 absolute right-0 top-0 md:mt-1 md:-mr-6" style={{ height: belowMd ? 62 : 95 }}>
                  <img alt="" src={EmailBot2} className=""/>
                  <img alt="" src={EmailBot3} className="scale-105"/>
                </span>
              </div>
              <div className="-mt-1">
                <Email
                  buttonText="Try for free"
                  handleSubmit={handleEmailSubmit}
                  componentClassName="flex-row md:space-y-0"
                  inputClassName="w-full rounded-l-[10px] rounded-r-none md:rounded-l-[10px] border border-neutral-500 md:border-r-0 font-semibold placeholder-white text-white"
                  buttonClassName="w-full md:w-auto rounded-r-[10px] md:rounded-[10px] md:rounded-l-none font-semibold border border-neutral-500 border-l-none"
                  inputStyle={{
                    background: "rgb(255, 255, 255, 0.12)",
                    height: belowMd ? "3rem" : "3.5rem",
                    minWidth: "15.5rem",
                  }}
                  buttonStyle={belowMd ? {
                    border: `double 0px transparent`,
                    background: "linear-gradient(90.29deg, #BBCDF6 11.18%, #E2C2FE 98.8%)",
                    color: "black",
                    backgroundOrigin: "border-box",
                    backgroundClip: "padding-box, border-box",
                    height: "3rem",
                  } : {
                    background: "linear-gradient(90.29deg, #BBCDF6 11.18%, #E2C2FE 98.8%)",
                    color: "black",
                    paddingLeft: "2.5rem",
                    paddingRight: "2.5rem",
                    height: "3.5rem",
                  }}
                />
                {submitError && (
                  <p className="text-sm text-error-200 text-left ml-2 mt-2 max-w-">
                    {submitError}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="text-center relative">
            <div
              className="w-full px-8 md:px-0"
              style={{ maxWidth: belowMd ? 307 : 614 }}
            >
              <img
                alt=""
                className="w-full object-contain"
                src={IQHero}
              />
            </div>
          </div>
        </div>
        {/* IQ CONNECTOR IMAGE */}
        <div className="mt-0 md:mt-10 flex items-center flex-col mx-auto">
          {/* for some reason textclip does not work properly if implemented as belowMd ? style1 : style 2 */}
          {belowMd && (
            <div
              className="mt-11 font-bold text-center flex flex-wrap"
              style={{
                fontFamily: "Gilroy",
                fontWeight: 500,
                maxWidth: "75%",
                fontSize: 20,
                background: "linear-gradient(118.11deg, #E3DBFD 65.74%, #BDA8FF 90.46%)",
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                webkitTextFillColor: "transparent",
              }}
            >
              What if AI knows your money better than you do?
            </div>
          )}
          {!belowMd && (
            <div
              className="mt-11 font-bold text-center flex flex-wrap mx-10"
              style={{
                fontFamily: "Gilroy",
                maxWidth: "57%",
                fontSize: 36,
                fontWeight: 500,
                background: "linear-gradient(90deg, #FFFFFF 46.15%, #BBA7F0 100%)",
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                webkitTextFillColor: "transparent",
              }}
            >
              What if AI knows your money better than you do?
            </div>
          )}
          <div style={{ position: 'relative', width: '100vw', height: 'auto', marginTop: "2rem", aspectRatio: "16 / 9" }}>
            {!videoPlaying && (
              <img
                src={VideoStartImage}
                alt="Click to play"
                onClick={handlePlay}
                style={{
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                  objectFit: 'cover',
                }}
              />
            )}
            <video
              ref={videoRef}
              src={TomoIQVideo}
              onEnded={() => setVideoPlaying(false)}
              style={{
                display: videoPlaying ? 'block' : 'none',
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        </div>
        {/* PRODUCT CARDS SECTION */}
        <Box
          sx={{
            marginTop: belowMd ? "40px" : "80px",
            marginX: "24px",
            width: belowMd ? undefined : "80vw",
            display: "flex",
            flexDirection: "column",
            gap: belowMd ? "40px" : "80px",
          }}
        >
          <ActionCard
            title={<h2>Apply to your <span className="italic">perfect</span> credit card in 0 clicks<span className="text-boost-primary-300">.</span></h2>}
            subtitle={<p>AI agents that apply <span className="italic">for</span> you.</p>}
            paragraph="Tomo’s AI agents scan your credit, match you to the best offers, and apply on your behalf—instantly and securely. No forms. No confusion. Just the right card, right away."
            ctaText="Try for Free"
            heroImage={ActionCard1}
            heroImageMobile={ActionCard1}
            imageClass="h-auto sm:w-4/5 md:w-5/6 lg:w-[557px] scale-110"
            onClickButton={() => handleActionCardButtonClick("card1")}
            belowMd={belowMd}
          />
          <ActionCard
            title={<h2>Raise and fix your credit score<span className="text-boost-primary-300">.</span></h2>}
            subtitle="Your credit future, powered by AI."
            paragraph="Whether you’re rebuilding credit, saving for a home, or just trying to breathe easier financially, Tomo is your always-on partner. It learns with you, plans with you, and takes action—so your goals stop feeling far away."
            ctaText="Try for Free"
            heroImage={ActionCard2}
            heroImageMobile={ActionCard2}
            imageClass="w-full h-auto object-contain"
            reversed
            onClickButton={() => handleActionCardButtonClick("card2")}
            belowMd={belowMd}
          />
          <ActionCard
            title={<h2>Manage your finances and never have a late bill again<span className="text-boost-primary-300">.</span></h2>}
            subtitle={<p>AI agents that <span className="italic">run your financial life.</span></p>}
            paragraph="Tomo’s autonomous agents monitor your accounts, move money where it’s needed, and pay your bills before they’re due. You stay in control—but the agents handle the heavy lifting."
            ctaText="Try for Free"
            heroImage={ActionCard3}
            heroImageMobile={ActionCard3}
            imageClass="w-full h-auto object-contain"
            onClickButton={() => handleActionCardButtonClick("card3")}
            belowMd={belowMd}
          />
          <ActionCard
            title={<h2>Invest and save your money while you sleep<span className="text-boost-primary-300">.</span></h2>}
            subtitle="Smarter decisions, automatically."
            paragraph="Tomo’s AI agents look for chances to grow your savings, invest your extra funds, and coach your spending habits in real-time. It’s like having a team of financial experts working behind the scenes 24/7."
            ctaText="Try for Free"
            heroImage={ActionCard4}
            heroImageMobile={ActionCard4}
            imageClass="w-full h-auto object-contain"
            reversed
            onClickButton={() => handleActionCardButtonClick("card4")}
            belowMd={belowMd}
          />
        </Box>
        {/* TESTIMONIAL CARDS SECTION */}
        <div
          className="mt-10 md:mt-20 flex flex-col justify-center items-center mx-auto"
          style={{ maxWidth: 1300 }}
        >
          <div className="md:mt-10 font-bold text-2xl text-white">Hear what others have to say</div>
          <div className="flex overflow-x-scroll hide-x-scrollbar shrink-0 w-full">
            <div className="mx-6 md:mx-10 my-6 md:mb-10 flex self-start overflow-visible">
              {
                [TestimonialCard1, TestimonialCard2, TestimonialCard3, TestimonialCard4]
                  .map((imageSrc, idx) => (
                    <img
                      alt=""
                      src={imageSrc}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`testimonial-${idx}`}
                      style={{ width: 295, height: "auto" }}
                      className="pr-6 box-content transform hover:scale-110 transition-transform"
                    />
                  ))
              }
            </div>
          </div>
        </div>
        {/* SOCIAL PROOF CAROUSEL */}
        <div className="my-10 md:my-20 py-8 bg-neutral-300" style={{ maxWidth: "100vw" }}>
          <AutoplayCarousel
            titleText="As mentioned in"
            duplicationFactor={3}
            totalWidthPreload={7752} // will need to change this if adding/removing image
            items={socialProofImages.map((item) => {
              return (
                <div className="" style={{ height: 30 }}>
                  <img src={item.img} alt={item.alt} className="" />
                </div>
              );
            })}
          />
        </div>
        <div className="py-10 text-center">© 2025 tomocredit.com</div>
      </div>
      <TomoIQWaitlistModal
        isOpen={renderWaitlistModal}
        onClose={() => setRenderWaitlistModal(false)}
      />
      {/* this is to make the overflow section black */}
      <div className="w-screen h-screen fixed" style={{ backgroundColor: "#1F1D24", zIndex: -1 }} />
    </div>
  );
};

export default IQLanding;